import { isBoolean, isNumber, isPlainObject, isString } from 'lodash';
import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { maskValue } from './helpers/maskValue';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';

const CustomInputSimple: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const baseScheme = baseInputValidationScheme;
  const validationScheme = useMemo(
    () => ({
      ...baseScheme,
      properties: {
        ...baseScheme.properties,
        initialValue: {
          type: 'custom',
          validator: (v: any) => isBoolean(v) || isNumber(v) || isString(v),
        },
        value: {
          type: 'custom',
          validator: (v: any) => isBoolean(v) || isNumber(v) || isString(v),
        },
        mask: {
          type: 'custom',
          validator: (v: any) => {
            if (isPlainObject(v)) {
              if (isString(v.exp) && (!v.flags || isString(v.flags))) return true;
              return false;
            } else if (isString(v)) {
              return true;
            } else {
              return false;
            }
          },
        },
      },
    }),
    [baseScheme]
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const {
    name,
    type,
    children,
    required,
    mask,
    validation,
    initialValue,
    value,
    formOnly,
    label,
    description,
    placeholder,
    selector,
    autoComplete,
  } = tagProps;
  const formContextInput = name ? context.form?.inputs[name] : undefined;

  const [valueState, setValueState] = useState<TInputState['value'] | undefined>(
    value || initialValue || formContextInput?.value || ''
  );
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [focused, setFocused] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-input',
      label,
      description,
      placeholder,
      type,
      value: value || initialValue,
      errors: [],
      touched: !!value,
      required,
      validation,
      initialValue,
      formOnly,
      reset: false,
      autoComplete,
    }),
    [formOnly, initialValue, name, required, validation, type, value, autoComplete]
  );

  // useEffect(() => {
  //   if (!value) setValueState(initialValue);
  // }, [tagProps])

  useEffect(() => {
    if (context.form) {
      if (!formContextInput) {
        context.form.handleSetInput(name, initialState);
      } else {
        if (formContextInput.reset) {
          context.form.handleSetInput(name, initialState);
          setValueState(initialValue);
        }
      }
    }
  }, [context.form, formContextInput, initialState, initialValue, name]);

  const allowedTypes = useMemo(
    () => [
      'button',
      'text',
      'color',
      'date',
      'datetime-local',
      'email',
      'number',
      'password',
      'search',
      'tel',
      'time',
      'url',
      'week',
      'month',
      'select-one',
      'select-multiple',
      'checkbox',
      'radio',
      'textarea',
    ],
    []
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!context.form || value) return;
      const inputType = e.target.type;
      const inputValue = e.target.value === '' ? undefined : e.target.value;
      if (!allowedTypes.includes(inputType)) {
        console.warn(`Type ${inputType} is not supported!`);
        return;
      }
      const isMaskable =
        !inputType ||
        ['text', 'search', 'email', 'password', 'tel', 'url', 'number'].includes(inputType);

      if (
        !focused &&
        isString(inputValue) &&
        isString(valueState) &&
        Math.abs(inputValue.length - valueState.length) > 1 &&
        autoComplete === 'off'
      )
        return;
      const newValue = isMaskable && mask && inputValue ? maskValue(inputValue, mask) : inputValue;
      setValueState(newValue);
      clearTimeout(timer);
      setTimer(setTimeout(() => context.form?.handleSetValue(name, newValue), 300));
    },
    [allowedTypes, autoComplete, context.form, focused, mask, name, timer, value, valueState]
  );

  const handleFocus = useCallback(() => {
    if (!focused) setFocused(true);
  }, [focused]);

  const handleBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (focused) setFocused(false);
      if (!formContextInput || value) return;
      if (valueState !== formContextInput.value) {
        context.form?.handleSetValue(name, valueState);
      }
    },
    [context.form, formContextInput, name, value, valueState]
  );

  const handleCleanup = useCallback(() => {
    if (value) return;
    setValueState(null);
    context.form?.handleSetValue(name);
  }, [context.form, name, value]);

  const handleShowHelp = useCallback(() => {
    setShowHelp(!showHelp);
  }, [showHelp]);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      value: valueState,
      label,
      description,
      placeholder,
      selector,
      showHelp,
      handleChange,
      handleFocus,
      handleBlur,
      handleCleanup,
      handleShowHelp,
      autoComplete,
    }),
    [
      formContextInput?.errors,
      formContextInput?.touched,
      handleBlur,
      handleChange,
      handleCleanup,
      handleShowHelp,
      initialState.type,
      initialValue,
      name,
      required,
      validation,
      valueState,
      description,
      label,
      placeholder,
      selector,
      autoComplete,
      showHelp,
      focused,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return null;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputSimpleContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputSimple.displayName = 'CustomInputSimple';
export default CustomInputSimple;
